/**
 * Operations screen (M8) — the hospitality Operations Management command centre.
 *
 * A real-time execution/control layer with six tabs:
 *   Overview · Tasks · Turnovers · Team Activity · Issues · Checklists
 *
 * Overview (default) is an owner-focused command centre: KPI cards (arrivals /
 * checkouts / units ready / open / overdue / blocked, several clickable to jump
 * to a filtered Tasks tab), property readiness, today's operations timeline, a
 * needs-attention / guest-impact list, a department snapshot, and operational
 * performance over the shared AVOS period selector. Every figure is DERIVED
 * from existing reservation / unit / property / task data — nothing fabricated.
 *
 * All backend access goes through the fetch-based `../services.js` layer (web
 * boundary: no `@avos/core-api` import). API error envelopes surface through the
 * shared accessible {@link UserMessage} component. SSR-safe: the initial render
 * is a pure loading state; every network call and any `Date` read runs inside an
 * effect or event handler.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  UserMessage,
  toUserMessage,
  type UserMessageModel,
} from "../../components/UserMessage.js";
import {
  apiCreateChecklist,
  apiCreateIssue,
  apiCreateOpsTask,
  apiDeleteChecklist,
  apiDeleteOpsTask,
  apiListChecklists,
  apiListIssues,
  apiListOpsTasks,
  apiListProperties,
  apiListTurnovers,
  apiListUnits,
  apiOpsDepartments,
  apiOpsOverview,
  apiOpsPerformance,
  apiOpsPropertyComparison,
  apiOpsSummary,
  apiOpsTeam,
  apiUpdateIssueStatus,
  apiUpdateOpsTaskStatus,
  apiUpdateTurnoverStage,
  asError,
  type OpsChecklistTemplate,
  type OpsDepartment,
  type OpsDepartmentSnapshot,
  type OpsIssue,
  type OpsOverview,
  type OpsPerformance,
  type OpsSummary,
  type OpsTask,
  type OpsTaskKind,
  type OpsTaskPriority,
  type OpsTaskSource,
  type OpsTaskStatus,
  type OpsPropertyComparisonRow,
  type OpsTeamMember,
  type OpsTurnover,
  type OpsTurnoverStage,
  type OpsUnitReadiness,
  type Property,
  type Unit,
} from "../services.js";

// ===========================================================================
// Labels + helpers
// ===========================================================================

const DEPARTMENTS: readonly { readonly value: OpsDepartment; readonly label: string }[] = [
  { value: "housekeeping", label: "Housekeeping" },
  { value: "maintenance", label: "Maintenance" },
  { value: "kitchen", label: "Kitchen" },
  { value: "guest_services", label: "Guest Services" },
  { value: "front_office", label: "Front Office" },
  { value: "security", label: "Security" },
  { value: "sales", label: "Sales" },
  { value: "marketing", label: "Marketing" },
  { value: "finance", label: "Finance" },
  { value: "management", label: "Management" },
  { value: "other", label: "Other" },
];

const KINDS: readonly { readonly value: OpsTaskKind; readonly label: string }[] = [
  { value: "housekeeping", label: "Housekeeping" },
  { value: "maintenance", label: "Maintenance" },
  { value: "inspection", label: "Inspection" },
  { value: "turnover", label: "Turnover" },
  { value: "restock", label: "Restock" },
];

const PRIORITIES: readonly { readonly value: OpsTaskPriority; readonly label: string }[] = [
  { value: "urgent", label: "Urgent" },
  { value: "high", label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low", label: "Low" },
];

const STATUSES: readonly { readonly value: OpsTaskStatus; readonly label: string }[] = [
  { value: "open", label: "Open" },
  { value: "assigned", label: "Assigned" },
  { value: "in_progress", label: "In progress" },
  { value: "blocked", label: "Blocked" },
  { value: "done", label: "Done" },
  { value: "cancelled", label: "Cancelled" },
];

const SOURCES: readonly { readonly value: OpsTaskSource; readonly label: string }[] = [
  { value: "manual", label: "Manual" },
  { value: "booking", label: "Booking-triggered" },
  { value: "turnover", label: "Checkout / Turnover" },
  { value: "guest_request", label: "Guest Request" },
  { value: "maintenance_issue", label: "Maintenance Issue" },
  { value: "automation", label: "Automation" },
];

const TURNOVER_STAGES: readonly { readonly value: OpsTurnoverStage; readonly label: string }[] = [
  { value: "checkout_due", label: "Checkout Due" },
  { value: "dirty", label: "Dirty" },
  { value: "cleaning", label: "Cleaning" },
  { value: "inspection", label: "Inspection" },
  { value: "ready", label: "Ready" },
  { value: "blocked", label: "Blocked" },
];

const ISSUE_STATUSES = ["open", "in_progress", "blocked", "resolved", "closed"] as const;

function labelOf<T extends string>(
  list: readonly { readonly value: T; readonly label: string }[],
  value: string,
): string {
  return list.find((x) => x.value === value)?.label ?? value;
}

const deptLabel = (v: string): string => labelOf(DEPARTMENTS, v);
const priorityLabel = (v: string): string => labelOf(PRIORITIES, v);
const statusLabel = (v: string): string => labelOf(STATUSES, v);
const sourceLabel = (v: string): string => labelOf(SOURCES, v);
const stageLabel = (v: string): string => labelOf(TURNOVER_STAGES, v);

/** Map a priority to the shared status-pill severity class. */
function priorityPill(priority: string): string {
  switch (priority) {
    case "urgent":
      return "avos-status avos-status--critical";
    case "high":
      return "avos-status avos-status--attention";
    case "low":
      return "avos-status avos-status--pending";
    default:
      return "avos-status";
  }
}

/** Map a task status to the shared status-pill severity class. */
function statusPill(status: string): string {
  switch (status) {
    case "done":
      return "avos-status avos-status--ok";
    case "blocked":
      return "avos-status avos-status--critical";
    case "in_progress":
      return "avos-status avos-status--attention";
    case "cancelled":
      return "avos-status avos-status--pending";
    default:
      return "avos-status";
  }
}

/** Map a turnover/readiness stage to a severity class. */
function stagePill(stage: string): string {
  switch (stage) {
    case "ready":
      return "avos-status avos-status--ok";
    case "blocked":
      return "avos-status avos-status--critical";
    case "cleaning":
    case "inspection":
      return "avos-status avos-status--attention";
    case "unknown":
      return "avos-status avos-status--pending";
    default:
      return "avos-status";
  }
}

/** Parse `YYYY-MM-DD` to a local midnight Date, or null. */
function parseDate(iso: string | null): Date | null {
  if (iso === null || iso.length < 10) {
    return null;
  }
  const [y, m, d] = iso.slice(0, 10).split("-").map((n) => Number.parseInt(n, 10));
  if (y === undefined || m === undefined || d === undefined) {
    return null;
  }
  return new Date(y, m - 1, d);
}

/** Format a `YYYY-MM-DD` (or ISO) for compact display, or em dash. */
function formatDate(iso: string | null): string {
  const d = parseDate(iso);
  if (d === null) {
    return "—";
  }
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

/** Format an ISO timestamp as a short time (HH:MM), or em dash. */
function formatTime(iso: string | null): string {
  if (iso === null) {
    return "—";
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "—";
  }
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
}

/** Whether a task is overdue relative to today (SSR-safe pure compare). */
function taskOverdue(task: OpsTask, todayIso: string): boolean {
  if (task.status === "done" || task.status === "cancelled" || task.dueOn === null) {
    return false;
  }
  return task.dueOn < todayIso;
}

/** Format a 0..1 rate as a percentage, or an em dash for null. */
function formatRate(rate: number | null): string {
  return rate === null ? "—" : `${Math.round(rate * 100)}%`;
}

/** Format hours (number|null) to a friendly duration. */
function formatHours(hours: number | null): string {
  if (hours === null) {
    return "—";
  }
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`;
  }
  if (hours < 48) {
    return `${hours.toFixed(1)} h`;
  }
  return `${(hours / 24).toFixed(1)} d`;
}

/** Checklist progress as "done/total". */
function checklistProgress(items: readonly { done: boolean }[]): { done: number; total: number } {
  return { done: items.filter((i) => i.done).length, total: items.length };
}

// ===========================================================================
// Period selector (self-contained; reuses the shared accounts-periodfilter CSS)
// ===========================================================================

type PresetKey =
  | "this-month"
  | "last-month"
  | "this-quarter"
  | "last-quarter"
  | "this-year"
  | "all-time"
  | "custom";

interface PeriodRange {
  readonly preset: PresetKey;
  readonly label: string;
  readonly fromIso: string;
  readonly toIso: string;
}

const PERIOD_PRESETS: readonly { readonly key: PresetKey; readonly label: string }[] = [
  { key: "this-month", label: "This month" },
  { key: "last-month", label: "Last month" },
  { key: "this-quarter", label: "This quarter" },
  { key: "last-quarter", label: "Last quarter" },
  { key: "this-year", label: "This year" },
  { key: "all-time", label: "All time" },
  { key: "custom", label: "Custom range" },
];

/** ISO date from Y/M/D (1-based month). */
function isoDate(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

/**
 * Resolve a preset to a concrete range. Reads `new Date()`, so only call inside
 * an effect or handler (SSR-safe by construction). All-time uses a wide window.
 */
function resolvePeriod(preset: PresetKey, from?: string, to?: string): PeriodRange {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth() + 1;
  const d = now.getDate();
  const lastDay = (yy: number, mm: number): number => new Date(yy, mm, 0).getDate();
  const label = (k: PresetKey): string => PERIOD_PRESETS.find((p) => p.key === k)?.label ?? k;
  switch (preset) {
    case "this-month":
      return { preset, label: label(preset), fromIso: isoDate(y, m, 1), toIso: isoDate(y, m, lastDay(y, m)) };
    case "last-month": {
      const lm = m === 1 ? 12 : m - 1;
      const ly = m === 1 ? y - 1 : y;
      return { preset, label: label(preset), fromIso: isoDate(ly, lm, 1), toIso: isoDate(ly, lm, lastDay(ly, lm)) };
    }
    case "this-quarter": {
      const q = Math.floor((m - 1) / 3);
      const startM = q * 3 + 1;
      const endM = startM + 2;
      return { preset, label: label(preset), fromIso: isoDate(y, startM, 1), toIso: isoDate(y, endM, lastDay(y, endM)) };
    }
    case "last-quarter": {
      const q = Math.floor((m - 1) / 3);
      let startM = q * 3 + 1 - 3;
      let yy = y;
      if (startM < 1) {
        startM += 12;
        yy -= 1;
      }
      const endM = startM + 2;
      return { preset, label: label(preset), fromIso: isoDate(yy, startM, 1), toIso: isoDate(yy, endM, lastDay(yy, endM)) };
    }
    case "this-year":
      return { preset, label: label(preset), fromIso: isoDate(y, 1, 1), toIso: isoDate(y, 12, 31) };
    case "custom":
      return {
        preset,
        label: label(preset),
        fromIso: from ?? isoDate(y, m, 1),
        toIso: to ?? isoDate(y, m, d),
      };
    case "all-time":
    default:
      return { preset: "all-time", label: label("all-time"), fromIso: "2020-01-01", toIso: isoDate(y + 1, 12, 31) };
  }
}

/** A compact period dropdown (preset menu + custom from/to inputs). */
function PeriodSelector({
  period,
  onChange,
}: {
  readonly period: PeriodRange;
  readonly onChange: (next: PeriodRange) => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [from, setFrom] = useState(period.fromIso);
  const [to, setTo] = useState(period.toIso);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open && !customOpen) {
      return;
    }
    function onDoc(e: MouseEvent): void {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setCustomOpen(false);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open, customOpen]);

  function choose(key: PresetKey): void {
    if (key === "custom") {
      setOpen(false);
      setCustomOpen(true);
      return;
    }
    setOpen(false);
    onChange(resolvePeriod(key));
  }

  return (
    <div className="accounts-periodfilter" ref={rootRef}>
      <span className="accounts-periodfilter__caption" id="ops-period-label">Period</span>
      <div className="accounts-periodfilter__control">
        <button
          type="button"
          className="accounts-periodfilter__trigger"
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => {
            setOpen((v) => !v);
            setCustomOpen(false);
          }}
        >
          <span className="accounts-periodfilter__icon" aria-hidden="true">🗓</span>
          <span className="accounts-periodfilter__value">{period.label}</span>
          <span className="accounts-periodfilter__chevron" aria-hidden="true">▾</span>
        </button>
        {open ? (
          <ul className="accounts-periodfilter__menu" role="listbox" aria-labelledby="ops-period-label">
            {PERIOD_PRESETS.map((p) => (
              <li key={p.key} role="option" aria-selected={period.preset === p.key}>
                <button
                  type="button"
                  className={
                    period.preset === p.key
                      ? "accounts-periodfilter__option accounts-periodfilter__option--active"
                      : "accounts-periodfilter__option"
                  }
                  onClick={() => choose(p.key)}
                >
                  {p.label}
                  {period.preset === p.key ? (
                    <span className="accounts-periodfilter__tick" aria-hidden="true">✓</span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {customOpen ? (
          <div className="accounts-periodfilter__panel" role="dialog" aria-label="Custom date range">
            <p className="accounts-periodfilter__panel-title">Custom range</p>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="ops-period-from">From</label>
                <input id="ops-period-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div className="console-field">
                <label htmlFor="ops-period-to">To</label>
                <input id="ops-period-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
            </div>
            <div className="accounts-periodfilter__panel-actions">
              <button type="button" className="console-btn console-btn--ghost" onClick={() => setCustomOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="console-btn"
                disabled={from === "" || to === "" || from > to}
                onClick={() => {
                  setCustomOpen(false);
                  onChange(resolvePeriod("custom", from, to));
                }}
              >
                Apply
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ===========================================================================
// Overview: KPI cards
// ===========================================================================

/** A KPI card definition. */
interface Kpi {
  readonly key: string;
  readonly label: string;
  readonly value: number;
  readonly hint: string;
  readonly gold?: boolean;
  /** When set, clicking the card navigates to Tasks with this status filter. */
  readonly taskStatus?: OpsTaskStatus;
}

function KpiCards({
  overview,
  summary,
  onOpenTasks,
}: {
  readonly overview: OpsOverview | null;
  readonly summary: OpsSummary | null;
  readonly onOpenTasks: (status: OpsTaskStatus | "all") => void;
}): ReactElement {
  const arrivals = overview?.arrivalsToday.length ?? 0;
  const checkouts = overview?.checkoutsToday.length ?? 0;
  const unitsReady = overview?.unitsReady ?? 0;
  const unitsTracked = overview?.unitsTracked ?? 0;
  const open = summary?.open ?? 0;
  const assigned = summary?.assigned ?? 0;
  const inProgress = summary?.inProgress ?? 0;
  const overdue = summary?.overdue ?? 0;
  const blocked = summary?.blocked ?? 0;

  const cards: readonly Kpi[] = [
    { key: "arrivals", label: "Arrivals today", value: arrivals, hint: "From confirmed bookings" },
    { key: "checkouts", label: "Checkouts today", value: checkouts, hint: "From confirmed bookings" },
    {
      key: "ready",
      label: "Units ready",
      value: unitsReady,
      hint: unitsTracked > 0 ? `of ${unitsTracked} tracked` : "No readiness data yet",
    },
    { key: "open", label: "Open tasks", value: open + assigned + inProgress, hint: "Open, assigned or in progress", taskStatus: "open" },
    { key: "overdue", label: "Overdue tasks", value: overdue, hint: "Past due date", gold: overdue > 0 },
    { key: "blocked", label: "Blocked tasks", value: blocked, hint: "Cannot proceed", taskStatus: "blocked" },
  ];

  return (
    <div className="ops-kpis" role="list" aria-label="Operations key metrics">
      {cards.map((c) => {
        const clickable = c.taskStatus !== undefined;
        const className = c.gold === true ? "console-card console-card--gold" : "console-card";
        const inner = (
          <>
            <p className="console-card__label">{c.label}</p>
            <p className="console-card__value">{c.value}</p>
            <p className="console-card__hint">{c.hint}</p>
          </>
        );
        if (clickable) {
          return (
            <button
              key={c.key}
              type="button"
              role="listitem"
              className={`${className} ops-kpi-btn`}
              onClick={() => onOpenTasks(c.taskStatus ?? "all")}
            >
              {inner}
            </button>
          );
        }
        return (
          <div key={c.key} role="listitem" className={className}>
            {inner}
          </div>
        );
      })}
    </div>
  );
}

// ===========================================================================
// Overview: Property Readiness
// ===========================================================================

function PropertyReadiness({
  readiness,
}: {
  readonly readiness: readonly OpsUnitReadiness[];
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Property readiness">
      <h2 className="console-panel__title">Property readiness</h2>
      <p className="recon-caption">
        Operational status of units in active turnover. Units with no readiness
        signal are not shown here rather than assumed ready.
      </p>
      {readiness.length > 0 ? (
        <div className="ops-readiness-grid">
          {readiness.map((r) => (
            <article
              key={r.turnoverId ?? r.unitId}
              className={r.atRisk ? "console-card ops-readycard ops-readycard--risk" : "console-card ops-readycard"}
            >
              <div className="ops-readycard__head">
                <div>
                  <p className="ops-readycard__unit">{r.unitName ?? "Unit"}</p>
                  <p className="ops-readycard__prop">{r.propertyName ?? "—"}</p>
                </div>
                <span className={stagePill(r.status)}>{stageLabel(r.status)}</span>
              </div>
              <dl className="ops-readycard__meta">
                <div>
                  <dt>Next arrival</dt>
                  <dd>{formatDate(r.nextArrivalOn)}</dd>
                </div>
                {r.atRisk ? (
                  <div>
                    <dt>Risk</dt>
                    <dd className="ops-risk-text">Not ready for arrival</dd>
                  </div>
                ) : null}
              </dl>
              {r.blockedReason !== null ? (
                <p className="ops-readycard__blocked">Blocked: {r.blockedReason}</p>
              ) : null}
            </article>
          ))}
        </div>
      ) : (
        <p className="console-empty">
          No units are in an active turnover right now. Readiness appears here
          when a checkout starts a turnover.
        </p>
      )}
    </section>
  );
}

// ===========================================================================
// Overview: Today's Operations timeline
// ===========================================================================

interface TimelineItem {
  readonly key: string;
  readonly time: string | null;
  readonly sortTime: number;
  readonly title: string;
  readonly place: string;
  readonly who: string;
  readonly statusNode: ReactNode;
}

function TodayTimeline({
  overview,
  tasks,
  todayIso,
}: {
  readonly overview: OpsOverview | null;
  readonly tasks: readonly OpsTask[];
  readonly todayIso: string;
}): ReactElement {
  const items = useMemo<TimelineItem[]>(() => {
    const out: TimelineItem[] = [];
    // Arrivals / checkouts (from reservations) — no clock time, order first/last.
    for (const a of overview?.arrivalsToday ?? []) {
      out.push({
        key: `arr-${a.reservationId}`,
        time: "Arrival",
        sortTime: 15 * 60,
        title: `Arrival — ${a.guestName ?? "Guest"}`,
        place: `${a.propertyName ?? "—"}${a.unitName ? ` · ${a.unitName}` : ""}`,
        who: "Front Office",
        statusNode: <span className="avos-status avos-status--attention">Arriving</span>,
      });
    }
    for (const c of overview?.checkoutsToday ?? []) {
      out.push({
        key: `chk-${c.reservationId}`,
        time: "Checkout",
        sortTime: 11 * 60,
        title: `Checkout — ${c.guestName ?? "Guest"}`,
        place: `${c.propertyName ?? "—"}${c.unitName ? ` · ${c.unitName}` : ""}`,
        who: "Housekeeping",
        statusNode: <span className="avos-status">Departing</span>,
      });
    }
    // Tasks due today or created today / in progress.
    for (const t of tasks) {
      const dueToday = t.dueOn === todayIso;
      const inProgress = t.status === "in_progress";
      if (!dueToday && !inProgress) {
        continue;
      }
      out.push({
        key: `task-${t.id}`,
        time: dueToday ? "Due today" : "In progress",
        sortTime: dueToday ? 12 * 60 : 13 * 60,
        title: t.title,
        place: `${t.propertyName ?? "—"}${t.unitName ? ` · ${t.unitName}` : ""}`,
        who: deptLabel(t.department),
        statusNode: <span className={statusPill(t.status)}>{statusLabel(t.status)}</span>,
      });
    }
    return out.sort((a, b) => a.sortTime - b.sortTime);
  }, [overview, tasks, todayIso]);

  return (
    <section className="console-panel" aria-label="Today's operations">
      <h2 className="console-panel__title">Today's operations</h2>
      {items.length > 0 ? (
        <ol className="ops-timeline">
          {items.map((it) => (
            <li key={it.key} className="ops-timeline__item">
              <span className="ops-timeline__time">{it.time}</span>
              <span className="ops-timeline__dot" aria-hidden="true" />
              <div className="ops-timeline__body">
                <p className="ops-timeline__title">{it.title}</p>
                <p className="ops-timeline__meta">
                  {it.place} · {it.who}
                </p>
              </div>
              <span className="ops-timeline__status">{it.statusNode}</span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="console-empty">Nothing scheduled or in progress for today.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Overview: Needs Attention / Guest Impact
// ===========================================================================

function NeedsAttention({
  tasks,
  issues,
  readiness,
  todayIso,
  onView,
}: {
  readonly tasks: readonly OpsTask[];
  readonly issues: readonly OpsIssue[];
  readonly readiness: readonly OpsUnitReadiness[];
  readonly todayIso: string;
  readonly onView: () => void;
}): ReactElement {
  const rows = useMemo(() => {
    const out: { key: string; label: string; detail: string; severity: string }[] = [];
    for (const t of tasks) {
      if (t.status === "blocked") {
        out.push({ key: `b-${t.id}`, label: `Blocked: ${t.title}`, detail: `${deptLabel(t.department)}${t.blockedReason ? ` · ${t.blockedReason}` : ""}`, severity: "critical" });
      } else if (taskOverdue(t, todayIso)) {
        out.push({ key: `o-${t.id}`, label: `Overdue: ${t.title}`, detail: `${deptLabel(t.department)} · due ${formatDate(t.dueOn)}`, severity: "attention" });
      } else if (t.priority === "urgent" && t.status !== "done") {
        out.push({ key: `u-${t.id}`, label: `Urgent: ${t.title}`, detail: deptLabel(t.department), severity: "critical" });
      }
    }
    for (const is of issues) {
      if (is.status === "resolved" || is.status === "closed") {
        continue;
      }
      if (is.guestImpact || is.priority === "urgent" || is.priority === "high") {
        out.push({
          key: `i-${is.id}`,
          label: `${is.guestImpact ? "Guest-impacting issue" : "Issue"}: ${is.title}`,
          detail: `${is.unitName ?? is.propertyName ?? "—"} · ${priorityLabel(is.priority)}`,
          severity: is.guestImpact ? "critical" : "attention",
        });
      }
    }
    for (const r of readiness) {
      if (r.atRisk) {
        out.push({
          key: `r-${r.unitId}`,
          label: `Unit not ready for arrival: ${r.unitName ?? "Unit"}`,
          detail: `${r.propertyName ?? "—"} · ${stageLabel(r.status)} · arrives ${formatDate(r.nextArrivalOn)}`,
          severity: "critical",
        });
      }
    }
    // Critical first.
    return out.sort((a, b) => (a.severity === "critical" ? 0 : 1) - (b.severity === "critical" ? 0 : 1));
  }, [tasks, issues, readiness, todayIso]);

  return (
    <section className="console-panel" aria-label="Needs attention">
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">Needs attention</h2>
        {rows.length > 0 ? <span className="crm-group__count">{rows.length}</span> : null}
      </div>
      {rows.length > 0 ? (
        <ul className="ops-attention">
          {rows.map((r) => (
            <li key={r.key} className={`ops-attention__item ops-attention__item--${r.severity}`}>
              <span className="ops-attention__dot" aria-hidden="true" />
              <div className="ops-attention__body">
                <p className="ops-attention__label">{r.label}</p>
                <p className="ops-attention__detail">{r.detail}</p>
              </div>
              <button type="button" className="console-btn console-btn--ghost" onClick={onView}>
                Review
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="console-empty">Nothing needs your attention right now — all clear.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Overview: Department Snapshot
// ===========================================================================

function DepartmentSnapshotPanel({
  departments,
  onOpenDepartment,
}: {
  readonly departments: readonly OpsDepartmentSnapshot[];
  readonly onOpenDepartment: (dept: OpsDepartment) => void;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Department snapshot">
      <h2 className="console-panel__title">Department snapshot</h2>
      {departments.length > 0 ? (
        <div className="ops-dept-grid">
          {departments.map((d) => (
            <button
              key={d.department}
              type="button"
              className="console-card ops-dept-card"
              onClick={() => onOpenDepartment(d.department)}
            >
              <p className="console-card__label">{deptLabel(d.department)}</p>
              <div className="ops-dept-counts">
                <span>{d.openTotal} open</span>
                <span>{d.inProgress} in progress</span>
                <span>{d.completed} done</span>
                {d.overdue > 0 ? <span className="ops-dept-counts__warn">{d.overdue} overdue</span> : null}
                {d.blocked > 0 ? <span className="ops-dept-counts__warn">{d.blocked} blocked</span> : null}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <p className="console-empty">No departmental work recorded yet.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Overview: Operational Performance
// ===========================================================================

function PerformancePanel({
  perf,
  period,
  onPeriodChange,
}: {
  readonly perf: OpsPerformance | null;
  readonly period: PeriodRange;
  readonly onPeriodChange: (next: PeriodRange) => void;
}): ReactElement {
  const hasData = perf !== null && (perf.tasksCreated > 0 || perf.tasksCompleted > 0);
  const metrics: readonly { label: string; value: string }[] =
    perf === null
      ? []
      : [
          { label: "Task completion rate", value: formatRate(perf.completionRate) },
          { label: "On-time completion", value: formatRate(perf.onTimeRate) },
          { label: "On-time turnover rate", value: formatRate(perf.onTimeTurnoverRate) },
          { label: "Avg task completion", value: formatHours(perf.avgCompletionHours) },
          { label: "Avg issue resolution", value: formatHours(perf.avgIssueResolutionHours) },
          { label: "Overdue now", value: String(perf.overdueNow) },
        ];
  return (
    <section className="console-panel" aria-label="Operational performance">
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">Operational performance</h2>
        <PeriodSelector period={period} onChange={onPeriodChange} />
      </div>
      {hasData ? (
        <div className="ops-perf-grid">
          {metrics.map((m) => (
            <div key={m.label} className="console-card console-card--muted">
              <p className="console-card__label">{m.label}</p>
              <p className="console-card__value">{m.value}</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="console-empty">
          Not enough operational history in this period yet. Metrics appear here
          as tasks, turnovers and issues are completed.
        </p>
      )}
    </section>
  );
}

// ===========================================================================
// Overview: Property Performance comparison (portfolio view only)
// ===========================================================================

function PropertyComparison({
  rows,
  onOpenProperty,
}: {
  readonly rows: readonly OpsPropertyComparisonRow[];
  readonly onOpenProperty: (propertyId: string) => void;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Property performance">
      <h2 className="console-panel__title">Property performance</h2>
      <p className="recon-caption">
        Portfolio-wide operational scorecard. Select a row to scope Operations to
        that property.
      </p>
      {rows.length > 0 ? (
        <div className="crm-table-wrap">
          <table className="console-table">
            <caption className="console-sr-only">Per-property operational comparison</caption>
            <thead>
              <tr>
                <th scope="col">Property</th>
                <th scope="col">Ready units</th>
                <th scope="col">Open tasks</th>
                <th scope="col">Overdue</th>
                <th scope="col">Blocked</th>
                <th scope="col">On-time completion</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.propertyId} className="ops-comp-row" onClick={() => onOpenProperty(r.propertyId)}>
                  <td>
                    <button type="button" className="crm-rowbtn" onClick={(e) => { e.stopPropagation(); onOpenProperty(r.propertyId); }}>
                      {r.propertyName ?? "Property"}
                    </button>
                  </td>
                  <td className="recon-num">{r.trackedUnits > 0 ? `${r.readyUnits}/${r.trackedUnits}` : "—"}</td>
                  <td className="recon-num">{r.openTasks}</td>
                  <td className="recon-num">{r.overdueTasks > 0 ? <span className="ops-overdue">{r.overdueTasks}</span> : r.overdueTasks}</td>
                  <td className="recon-num">{r.blockedTasks}</td>
                  <td className="recon-num">{formatRate(r.onTimeCompletionRate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="console-empty">No properties to compare yet.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Tasks tab: filters + board + New Task modal
// ===========================================================================

interface TaskFilters {
  readonly search: string;
  readonly status: OpsTaskStatus | "all";
  readonly department: OpsDepartment | "all";
  readonly priority: OpsTaskPriority | "all";
  readonly source: OpsTaskSource | "all";
  readonly assignee: string;
  readonly propertyId: string;
}

const EMPTY_TASK_FILTERS: TaskFilters = {
  search: "",
  status: "all",
  department: "all",
  priority: "all",
  source: "all",
  assignee: "",
  propertyId: "",
};

function NewTaskModal({
  properties,
  units,
  checklists,
  onClose,
  onCreate,
  busy,
}: {
  readonly properties: readonly Property[];
  readonly units: readonly Unit[];
  readonly checklists: readonly OpsChecklistTemplate[];
  readonly onClose: () => void;
  readonly onCreate: (input: Parameters<typeof apiCreateOpsTask>[0]) => void;
  readonly busy: boolean;
}): ReactElement {
  const [title, setTitle] = useState("");
  const [department, setDepartment] = useState<OpsDepartment>("housekeeping");
  const [kind, setKind] = useState<OpsTaskKind>("housekeeping");
  const [priority, setPriority] = useState<OpsTaskPriority>("normal");
  const [propertyId, setPropertyId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [assignee, setAssignee] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [source, setSource] = useState<OpsTaskSource>("manual");
  const [notes, setNotes] = useState("");
  const [checklistId, setChecklistId] = useState("");

  const unitsForProperty = propertyId === "" ? units : units.filter((u) => u.propertyId === propertyId);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const t = title.trim();
    if (t === "") {
      return;
    }
    const tpl = checklists.find((c) => c.id === checklistId);
    onCreate({
      kind,
      title: t,
      department,
      priority,
      source,
      ...(propertyId !== "" ? { propertyId } : {}),
      ...(unitId !== "" ? { unitId } : {}),
      ...(assignee.trim() !== "" ? { assignee: assignee.trim() } : {}),
      ...(dueOn !== "" ? { dueOn } : {}),
      ...(notes.trim() !== "" ? { notes: notes.trim() } : {}),
      ...(checklistId !== "" ? { checklistTemplateId: checklistId } : {}),
      ...(tpl !== undefined ? { checklistState: tpl.items.map((label) => ({ label, done: false })) } : {}),
    });
  }

  return (
    <div className="console-modal-scrim" role="presentation" onClick={onClose}>
      <div
        className="console-modal"
        role="dialog"
        aria-modal="true"
        aria-label="New task"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="console-modal__header">
          <h2 className="console-modal__title">New task</h2>
          <button type="button" className="console-iconbtn console-modal__close" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </header>
        <div className="console-modal__body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="ops-nt-title">Title</label>
                <input id="ops-nt-title" value={title} placeholder="e.g. Turnover clean unit 2" onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="console-field">
                <label htmlFor="ops-nt-dept">Department</label>
                <select id="ops-nt-dept" value={department} onChange={(e) => setDepartment(e.target.value as OpsDepartment)}>
                  {DEPARTMENTS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="ops-nt-kind">Kind</label>
                <select id="ops-nt-kind" value={kind} onChange={(e) => setKind(e.target.value as OpsTaskKind)}>
                  {KINDS.map((k) => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="ops-nt-prop">Property</label>
                <select
                  id="ops-nt-prop"
                  value={propertyId}
                  onChange={(e) => {
                    setPropertyId(e.target.value);
                    setUnitId("");
                  }}
                >
                  <option value="">—</option>
                  {properties.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="ops-nt-unit">Unit</label>
                <select id="ops-nt-unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                  <option value="">—</option>
                  {unitsForProperty.map((u) => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="ops-nt-priority">Priority</label>
                <select id="ops-nt-priority" value={priority} onChange={(e) => setPriority(e.target.value as OpsTaskPriority)}>
                  {PRIORITIES.map((p) => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="ops-nt-assignee">Assignee</label>
                <input id="ops-nt-assignee" value={assignee} placeholder="e.g. Meera R." onChange={(e) => setAssignee(e.target.value)} />
              </div>
              <div className="console-field">
                <label htmlFor="ops-nt-due">Due date</label>
                <input id="ops-nt-due" type="date" value={dueOn} onChange={(e) => setDueOn(e.target.value)} />
              </div>
              <div className="console-field">
                <label htmlFor="ops-nt-source">Source</label>
                <select id="ops-nt-source" value={source} onChange={(e) => setSource(e.target.value as OpsTaskSource)}>
                  {SOURCES.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="ops-nt-checklist">Checklist</label>
                <select id="ops-nt-checklist" value={checklistId} onChange={(e) => setChecklistId(e.target.value)}>
                  <option value="">None</option>
                  {checklists.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="console-field ops-field--grow">
                <label htmlFor="ops-nt-notes">Notes</label>
                <input id="ops-nt-notes" value={notes} placeholder="Optional notes" onChange={(e) => setNotes(e.target.value)} />
              </div>
            </div>
            <div className="console-modal__actions">
              <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="console-btn" disabled={busy || title.trim() === ""}>
                {busy ? "Creating…" : "Create task"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function TasksTab({
  tasks,
  todayIso,
  filters,
  onFilters,
  properties,
  onStatus,
  onDelete,
  onNewTask,
  statusBusyId,
  deletingId,
}: {
  readonly tasks: readonly OpsTask[];
  readonly todayIso: string;
  readonly filters: TaskFilters;
  readonly onFilters: (next: TaskFilters) => void;
  readonly properties: readonly Property[];
  readonly onStatus: (task: OpsTask, status: OpsTaskStatus) => void;
  readonly onDelete: (task: OpsTask) => void;
  readonly onNewTask: () => void;
  readonly statusBusyId: string | null;
  readonly deletingId: string | null;
}): ReactElement {
  const assignees = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) {
      if (t.assignee !== null && t.assignee.trim() !== "") {
        set.add(t.assignee);
      }
    }
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLowerCase();
    return tasks.filter((t) => {
      if (q !== "" && !`${t.title} ${t.notes ?? ""} ${t.assignee ?? ""}`.toLowerCase().includes(q)) {
        return false;
      }
      if (filters.status !== "all" && t.status !== filters.status) return false;
      if (filters.department !== "all" && t.department !== filters.department) return false;
      if (filters.priority !== "all" && t.priority !== filters.priority) return false;
      if (filters.source !== "all" && t.source !== filters.source) return false;
      if (filters.assignee !== "" && t.assignee !== filters.assignee) return false;
      if (filters.propertyId !== "" && t.propertyId !== filters.propertyId) return false;
      return true;
    });
  }, [tasks, filters]);

  function set<K extends keyof TaskFilters>(key: K, value: TaskFilters[K]): void {
    onFilters({ ...filters, [key]: value });
  }

  return (
    <section className="console-panel" aria-label="Operational tasks">
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">Tasks</h2>
        <button type="button" className="console-btn" onClick={onNewTask}>+ New task</button>
      </div>

      <div className="crm-filters">
        <div className="console-field crm-filters__search">
          <label htmlFor="ops-t-search">Search</label>
          <input id="ops-t-search" type="search" value={filters.search} placeholder="Title, notes, assignee" onChange={(e) => set("search", e.target.value)} />
        </div>
        <div className="console-field">
          <label htmlFor="ops-t-status">Status</label>
          <select id="ops-t-status" value={filters.status} onChange={(e) => set("status", e.target.value as TaskFilters["status"])}>
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="ops-t-dept">Department</label>
          <select id="ops-t-dept" value={filters.department} onChange={(e) => set("department", e.target.value as TaskFilters["department"])}>
            <option value="all">All departments</option>
            {DEPARTMENTS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="ops-t-priority">Priority</label>
          <select id="ops-t-priority" value={filters.priority} onChange={(e) => set("priority", e.target.value as TaskFilters["priority"])}>
            <option value="all">All priorities</option>
            {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="ops-t-source">Source</label>
          <select id="ops-t-source" value={filters.source} onChange={(e) => set("source", e.target.value as TaskFilters["source"])}>
            <option value="all">All sources</option>
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div className="console-field">
          <label htmlFor="ops-t-prop">Property</label>
          <select id="ops-t-prop" value={filters.propertyId} onChange={(e) => set("propertyId", e.target.value)}>
            <option value="">All properties</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        {assignees.length > 0 ? (
          <div className="console-field">
            <label htmlFor="ops-t-assignee">Assignee</label>
            <select id="ops-t-assignee" value={filters.assignee} onChange={(e) => set("assignee", e.target.value)}>
              <option value="">Anyone</option>
              {assignees.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
        ) : null}
      </div>

      <p className="crm-directory__count">Showing {filtered.length} of {tasks.length} tasks</p>

      {filtered.length > 0 ? (
        <div className="crm-table-wrap">
          <table className="console-table">
            <caption className="console-sr-only">Operational tasks</caption>
            <thead>
              <tr>
                <th scope="col">Task</th>
                <th scope="col">Department</th>
                <th scope="col">Property / unit</th>
                <th scope="col">Assignee</th>
                <th scope="col">Priority</th>
                <th scope="col">Due</th>
                <th scope="col">Source</th>
                <th scope="col">Status</th>
                <th scope="col"><span className="console-sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const overdue = taskOverdue(t, todayIso);
                const progress = t.checklistState.length > 0 ? checklistProgress(t.checklistState) : null;
                return (
                  <tr key={t.id}>
                    <td>
                      <span className="ops-task-title">{t.title}</span>
                      {progress !== null ? (
                        <span className="ops-checklist-badge">{progress.done}/{progress.total} checklist</span>
                      ) : null}
                      {t.blockedReason !== null ? (
                        <span className="ops-blocked-note">Blocked: {t.blockedReason}</span>
                      ) : null}
                    </td>
                    <td>{deptLabel(t.department)}</td>
                    <td>
                      {t.propertyName ?? "—"}
                      {t.unitName !== null ? <span className="ops-subtle"> · {t.unitName}</span> : null}
                    </td>
                    <td>{t.assignee ?? <span className="ops-subtle">Unassigned</span>}</td>
                    <td><span className={priorityPill(t.priority)}>{priorityLabel(t.priority)}</span></td>
                    <td className={overdue ? "ops-overdue" : undefined}>
                      {t.dueOn !== null ? formatDate(t.dueOn) : "—"}
                      {overdue ? <span className="ops-overdue-tag"> overdue</span> : null}
                    </td>
                    <td><span className="console-badge">{sourceLabel(t.source)}</span></td>
                    <td>
                      <label htmlFor={`ops-st-${t.id}`} className="console-sr-only">Status for {t.title}</label>
                      <select
                        id={`ops-st-${t.id}`}
                        value={t.status}
                        disabled={statusBusyId === t.id || t.status === "done" || t.status === "cancelled"}
                        onChange={(e) => onStatus(t, e.target.value as OpsTaskStatus)}
                      >
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="console-btn console-btn--ghost"
                        disabled={deletingId === t.id}
                        onClick={() => onDelete(t)}
                      >
                        {deletingId === t.id ? "…" : "Delete"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="console-empty">No tasks match these filters. Create one with “+ New task”.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Turnovers tab
// ===========================================================================

const TURNOVER_ORDER: readonly OpsTurnoverStage[] = [
  "checkout_due",
  "dirty",
  "cleaning",
  "inspection",
  "ready",
];

/** Human "time remaining" until ready-by, from now. */
function timeRemaining(readyByIso: string | null, now: Date): string {
  if (readyByIso === null) {
    return "—";
  }
  const rb = new Date(readyByIso);
  if (Number.isNaN(rb.getTime())) {
    return "—";
  }
  const ms = rb.getTime() - now.getTime();
  if (ms <= 0) {
    return "overdue";
  }
  const hours = Math.floor(ms / 3_600_000);
  if (hours < 1) {
    return `${Math.round(ms / 60_000)} min`;
  }
  if (hours < 24) {
    return `${hours} h`;
  }
  return `${Math.floor(hours / 24)} d`;
}

function TurnoversTab({
  turnovers,
  now,
  onStage,
  stageBusyId,
}: {
  readonly turnovers: readonly OpsTurnover[];
  readonly now: Date;
  readonly onStage: (tv: OpsTurnover, stage: OpsTurnoverStage) => void;
  readonly stageBusyId: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Turnovers">
      <h2 className="console-panel__title">Turnovers</h2>
      <p className="recon-caption">
        Checkout → Dirty → Cleaning → Inspection → Ready. A turnover links a
        checkout to the next arrival; a genuine blocker moves it to Blocked.
      </p>
      {turnovers.length > 0 ? (
        <div className="ops-turnover-grid">
          {turnovers.map((tv) => {
            const progress = tv.checklistState.length > 0 ? checklistProgress(tv.checklistState) : null;
            const remaining = timeRemaining(tv.readyBy, now);
            const urgent = remaining === "overdue" || (tv.nextArrivalOn !== null && parseDate(tv.nextArrivalOn) !== null && parseDate(tv.nextArrivalOn)!.getTime() <= now.getTime() && tv.stage !== "ready");
            return (
              <article key={tv.id} className={urgent ? "console-card ops-turnover ops-turnover--urgent" : "console-card ops-turnover"}>
                <div className="ops-turnover__head">
                  <div>
                    <p className="ops-turnover__unit">{tv.unitName ?? "Unit"}</p>
                    <p className="ops-turnover__prop">{tv.propertyName ?? "—"}</p>
                  </div>
                  <span className={stagePill(tv.stage)}>{stageLabel(tv.stage)}</span>
                </div>
                <ol className="ops-stage-track" aria-label="Turnover stages">
                  {TURNOVER_ORDER.map((s) => {
                    const idx = TURNOVER_ORDER.indexOf(s);
                    const curIdx = TURNOVER_ORDER.indexOf(tv.stage as OpsTurnoverStage);
                    const reached = tv.stage !== "blocked" && idx <= curIdx;
                    return (
                      <li key={s} className={reached ? "ops-stage ops-stage--done" : "ops-stage"}>
                        <span className="ops-stage__dot" aria-hidden="true" />
                        <span className="ops-stage__label">{stageLabel(s)}</span>
                      </li>
                    );
                  })}
                </ol>
                <dl className="ops-turnover__meta">
                  <div><dt>Checkout</dt><dd>{formatDate(tv.checkoutOn)}</dd></div>
                  <div><dt>Next arrival</dt><dd>{formatDate(tv.nextArrivalOn)}</dd></div>
                  <div><dt>Ready by</dt><dd>{formatTime(tv.readyBy)}</dd></div>
                  <div><dt>Time left</dt><dd className={remaining === "overdue" ? "ops-risk-text" : undefined}>{remaining}</dd></div>
                  <div><dt>Housekeeping</dt><dd>{tv.housekeepingAssignee ?? "—"}</dd></div>
                  {progress !== null ? <div><dt>Checklist</dt><dd>{progress.done}/{progress.total}</dd></div> : null}
                </dl>
                {tv.blockedReason !== null ? <p className="ops-readycard__blocked">Blocked: {tv.blockedReason}</p> : null}
                <div className="ops-turnover__actions">
                  <label htmlFor={`ops-tv-${tv.id}`} className="console-sr-only">Advance stage for {tv.unitName ?? "unit"}</label>
                  <select
                    id={`ops-tv-${tv.id}`}
                    value={tv.stage}
                    disabled={stageBusyId === tv.id}
                    onChange={(e) => onStage(tv, e.target.value as OpsTurnoverStage)}
                  >
                    {TURNOVER_STAGES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <p className="console-empty">No turnovers in progress. A checkout that needs the unit prepared creates one.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Team Activity tab
// ===========================================================================

function TeamTab({ team }: { readonly team: readonly OpsTeamMember[] }): ReactElement {
  return (
    <section className="console-panel" aria-label="Team activity">
      <h2 className="console-panel__title">Team activity</h2>
      <p className="recon-caption">
        Operational workload derived from task assignments. This reads only the
        task board — no HR or payroll records.
      </p>
      {team.length > 0 ? (
        <div className="crm-table-wrap">
          <table className="console-table">
            <caption className="console-sr-only">Team activity</caption>
            <thead>
              <tr>
                <th scope="col">Team member</th>
                <th scope="col">Current task</th>
                <th scope="col">Assigned today</th>
                <th scope="col">In progress</th>
                <th scope="col">Completed</th>
                <th scope="col">Overdue</th>
                <th scope="col">Next task</th>
              </tr>
            </thead>
            <tbody>
              {team.map((m) => (
                <tr key={m.assignee}>
                  <td className="ops-task-title">{m.assignee}</td>
                  <td>{m.currentTask ?? <span className="ops-subtle">Idle</span>}</td>
                  <td className="recon-num">{m.assignedToday}</td>
                  <td className="recon-num">{m.inProgress}</td>
                  <td className="recon-num">{m.completed}</td>
                  <td className="recon-num">{m.overdue > 0 ? <span className="ops-overdue">{m.overdue}</span> : m.overdue}</td>
                  <td>{m.nextTask ?? <span className="ops-subtle">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="console-empty">No assigned operational work yet. Assign tasks to see team activity here.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Issues tab
// ===========================================================================

function NewIssueModal({
  properties,
  units,
  onClose,
  onCreate,
  busy,
}: {
  readonly properties: readonly Property[];
  readonly units: readonly Unit[];
  readonly onClose: () => void;
  readonly onCreate: (input: Parameters<typeof apiCreateIssue>[0]) => void;
  readonly busy: boolean;
}): ReactElement {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("maintenance");
  const [priority, setPriority] = useState<OpsTaskPriority>("normal");
  const [propertyId, setPropertyId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [reportedBy, setReportedBy] = useState("");
  const [guestImpact, setGuestImpact] = useState(false);
  const [createTask, setCreateTask] = useState(true);

  const unitsForProperty = propertyId === "" ? units : units.filter((u) => u.propertyId === propertyId);

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const t = title.trim();
    if (t === "") return;
    onCreate({
      title: t,
      category,
      priority,
      guestImpact,
      createTask,
      ...(description.trim() !== "" ? { description: description.trim() } : {}),
      ...(propertyId !== "" ? { propertyId } : {}),
      ...(unitId !== "" ? { unitId } : {}),
      ...(reportedBy.trim() !== "" ? { reportedBy: reportedBy.trim() } : {}),
    });
  }

  return (
    <div className="console-modal-scrim" role="presentation" onClick={onClose}>
      <div className="console-modal" role="dialog" aria-modal="true" aria-label="Report issue" onClick={(e) => e.stopPropagation()}>
        <header className="console-modal__header">
          <h2 className="console-modal__title">Report issue</h2>
          <button type="button" className="console-iconbtn console-modal__close" aria-label="Close" onClick={onClose}>✕</button>
        </header>
        <div className="console-modal__body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="console-form__row">
              <div className="console-field ops-field--grow">
                <label htmlFor="ops-ni-title">Title</label>
                <input id="ops-ni-title" value={title} placeholder="e.g. AC not cooling" onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="console-field">
                <label htmlFor="ops-ni-cat">Category</label>
                <select id="ops-ni-cat" value={category} onChange={(e) => setCategory(e.target.value)}>
                  {["maintenance", "housekeeping", "safety", "appliance", "plumbing", "electrical", "guest_complaint", "other"].map((c) => (
                    <option key={c} value={c}>{c.replace("_", " ")}</option>
                  ))}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="ops-ni-priority">Priority</label>
                <select id="ops-ni-priority" value={priority} onChange={(e) => setPriority(e.target.value as OpsTaskPriority)}>
                  {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div className="console-form__row">
              <div className="console-field">
                <label htmlFor="ops-ni-prop">Property</label>
                <select id="ops-ni-prop" value={propertyId} onChange={(e) => { setPropertyId(e.target.value); setUnitId(""); }}>
                  <option value="">—</option>
                  {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="ops-ni-unit">Unit</label>
                <select id="ops-ni-unit" value={unitId} onChange={(e) => setUnitId(e.target.value)}>
                  <option value="">—</option>
                  {unitsForProperty.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              <div className="console-field">
                <label htmlFor="ops-ni-reporter">Reported by</label>
                <input id="ops-ni-reporter" value={reportedBy} onChange={(e) => setReportedBy(e.target.value)} />
              </div>
            </div>
            <div className="console-field ops-field--grow">
              <label htmlFor="ops-ni-desc">Description</label>
              <input id="ops-ni-desc" value={description} placeholder="Optional details" onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="console-form__row">
              <div className="console-field console-field--checkbox">
                <label htmlFor="ops-ni-guest">
                  <input id="ops-ni-guest" type="checkbox" checked={guestImpact} onChange={(e) => setGuestImpact(e.target.checked)} />
                  Affects a guest
                </label>
              </div>
              <div className="console-field console-field--checkbox">
                <label htmlFor="ops-ni-task">
                  <input id="ops-ni-task" type="checkbox" checked={createTask} onChange={(e) => setCreateTask(e.target.checked)} />
                  Also create a maintenance task
                </label>
              </div>
            </div>
            <div className="console-modal__actions">
              <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="console-btn" disabled={busy || title.trim() === ""}>
                {busy ? "Saving…" : "Report issue"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function IssuesTab({
  issues,
  onNewIssue,
  onStatus,
  statusBusyId,
}: {
  readonly issues: readonly OpsIssue[];
  readonly onNewIssue: () => void;
  readonly onStatus: (issue: OpsIssue, status: string) => void;
  readonly statusBusyId: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Operational issues">
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">Issues</h2>
        <button type="button" className="console-btn" onClick={onNewIssue}>+ Report issue</button>
      </div>
      <p className="recon-caption">
        Maintenance and operational problems. An issue is distinct from a task —
        it can spawn or link to a maintenance task and flags guest impact.
      </p>
      {issues.length > 0 ? (
        <div className="crm-table-wrap">
          <table className="console-table">
            <caption className="console-sr-only">Operational issues</caption>
            <thead>
              <tr>
                <th scope="col">Issue</th>
                <th scope="col">Category</th>
                <th scope="col">Property / unit</th>
                <th scope="col">Priority</th>
                <th scope="col">Reported</th>
                <th scope="col">Assignee</th>
                <th scope="col">Guest impact</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {issues.map((i) => (
                <tr key={i.id}>
                  <td>
                    <span className="ops-task-title">{i.title}</span>
                    {i.taskId !== null ? <span className="ops-checklist-badge">linked task</span> : null}
                  </td>
                  <td>{i.category.replace("_", " ")}</td>
                  <td>{i.unitName ?? i.propertyName ?? "—"}</td>
                  <td><span className={priorityPill(i.priority)}>{priorityLabel(i.priority)}</span></td>
                  <td>{formatDate(i.reportedAt)}</td>
                  <td>{i.assignee ?? <span className="ops-subtle">Unassigned</span>}</td>
                  <td>{i.guestImpact ? <span className="avos-status avos-status--critical">Guest</span> : <span className="ops-subtle">—</span>}</td>
                  <td>
                    <label htmlFor={`ops-is-${i.id}`} className="console-sr-only">Status for {i.title}</label>
                    <select
                      id={`ops-is-${i.id}`}
                      value={i.status}
                      disabled={statusBusyId === i.id}
                      onChange={(e) => onStatus(i, e.target.value)}
                    >
                      {ISSUE_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="console-empty">No issues reported. Use “+ Report issue” to log a maintenance problem.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Checklists tab
// ===========================================================================

function NewChecklistModal({
  properties,
  onClose,
  onCreate,
  busy,
}: {
  readonly properties: readonly Property[];
  readonly onClose: () => void;
  readonly onCreate: (input: Parameters<typeof apiCreateChecklist>[0]) => void;
  readonly busy: boolean;
}): ReactElement {
  const [name, setName] = useState("");
  const [department, setDepartment] = useState<OpsDepartment>("housekeeping");
  const [propertyId, setPropertyId] = useState("");
  const [itemsText, setItemsText] = useState("");

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const n = name.trim();
    const items = itemsText.split("\n").map((s) => s.trim()).filter((s) => s !== "");
    if (n === "" || items.length === 0) return;
    onCreate({
      name: n,
      department,
      items,
      ...(propertyId !== "" ? { propertyId } : {}),
    });
  }

  return (
    <div className="console-modal-scrim" role="presentation" onClick={onClose}>
      <div className="console-modal console-modal--confirm" role="dialog" aria-modal="true" aria-label="New checklist template" onClick={(e) => e.stopPropagation()}>
        <header className="console-modal__header">
          <h2 className="console-modal__title">New checklist template</h2>
          <button type="button" className="console-iconbtn console-modal__close" aria-label="Close" onClick={onClose}>✕</button>
        </header>
        <div className="console-modal__body">
          <form onSubmit={handleSubmit} noValidate>
            <div className="console-form__row">
              <div className="console-field ops-field--grow">
                <label htmlFor="ops-nc-name">Name</label>
                <input id="ops-nc-name" value={name} placeholder="e.g. Standard Turnover" onChange={(e) => setName(e.target.value)} />
              </div>
              <div className="console-field">
                <label htmlFor="ops-nc-dept">Department</label>
                <select id="ops-nc-dept" value={department} onChange={(e) => setDepartment(e.target.value as OpsDepartment)}>
                  {DEPARTMENTS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
            </div>
            <div className="console-field">
              <label htmlFor="ops-nc-prop">Property (optional)</label>
              <select id="ops-nc-prop" value={propertyId} onChange={(e) => setPropertyId(e.target.value)}>
                <option value="">All properties</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="console-field">
              <label htmlFor="ops-nc-items">Items (one per line)</label>
              <textarea
                id="ops-nc-items"
                className="crm-composer__textarea"
                value={itemsText}
                placeholder={"Strip & remake beds\nClean bathrooms\nRestock amenities"}
                onChange={(e) => setItemsText(e.target.value)}
              />
            </div>
            <div className="console-modal__actions">
              <button type="button" className="console-btn console-btn--ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="console-btn" disabled={busy || name.trim() === ""}>
                {busy ? "Saving…" : "Create template"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ChecklistsTab({
  templates,
  onNew,
  onDelete,
  deletingId,
}: {
  readonly templates: readonly OpsChecklistTemplate[];
  readonly onNew: () => void;
  readonly onDelete: (t: OpsChecklistTemplate) => void;
  readonly deletingId: string | null;
}): ReactElement {
  return (
    <section className="console-panel" aria-label="Checklist templates">
      <div className="accounts-panel-head">
        <h2 className="console-panel__title">Checklists</h2>
        <button type="button" className="console-btn" onClick={onNew}>+ New template</button>
      </div>
      <p className="recon-caption">
        Reusable checklist templates. Attach one to a task or turnover so staff
        complete each item before finishing the work.
      </p>
      {templates.length > 0 ? (
        <div className="ops-checklist-grid">
          {templates.map((t) => (
            <article key={t.id} className="console-card ops-checklist-card">
              <div className="ops-checklist-card__head">
                <div>
                  <p className="ops-task-title">{t.name}</p>
                  <p className="ops-subtle">{deptLabel(t.department)}</p>
                </div>
                <button
                  type="button"
                  className="console-btn console-btn--ghost"
                  disabled={deletingId === t.id}
                  onClick={() => onDelete(t)}
                >
                  {deletingId === t.id ? "…" : "Delete"}
                </button>
              </div>
              <ul className="ops-checklist-items">
                {t.items.map((item, idx) => (
                  <li key={`${t.id}-${idx}`}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      ) : (
        <p className="console-empty">No checklist templates yet. Create one with “+ New template”.</p>
      )}
    </section>
  );
}

// ===========================================================================
// Top-level screen
// ===========================================================================

type OpsTab = "overview" | "tasks" | "turnovers" | "team" | "issues" | "checklists";

const OPS_TABS: readonly { readonly key: OpsTab; readonly label: string }[] = [
  { key: "overview", label: "Overview" },
  { key: "tasks", label: "Tasks" },
  { key: "turnovers", label: "Turnovers" },
  { key: "team", label: "Team Activity" },
  { key: "issues", label: "Issues" },
  { key: "checklists", label: "Checklists" },
];

interface OpsData {
  readonly tasks: readonly OpsTask[];
  readonly summary: OpsSummary | null;
  readonly overview: OpsOverview | null;
  readonly team: readonly OpsTeamMember[];
  readonly departments: readonly OpsDepartmentSnapshot[];
  readonly turnovers: readonly OpsTurnover[];
  readonly issues: readonly OpsIssue[];
  readonly checklists: readonly OpsChecklistTemplate[];
  readonly properties: readonly Property[];
  readonly units: readonly Unit[];
  readonly comparison: readonly OpsPropertyComparisonRow[];
}

type LoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly data: OpsData }
  | { readonly kind: "error" };

/**
 * Load all Operations data, scoped to `propertyId` (empty string = portfolio
 * wide). Every scoped read passes the property through; properties/units/
 * checklists/comparison are always tenant-wide (the selector list, the form
 * option sources, and the portfolio scorecard).
 */
async function loadAll(propertyId: string): Promise<OpsData> {
  const scope = propertyId === "" ? undefined : propertyId;
  const [tasks, summaryR, overviewR, team, departments, turnovers, issues, checklists, properties, units, comparison] =
    await Promise.all([
      apiListOpsTasks(scope !== undefined ? { propertyId: scope } : {}),
      apiOpsSummary(scope),
      apiOpsOverview(scope !== undefined ? { propertyId: scope } : {}),
      apiOpsTeam(scope),
      apiOpsDepartments(scope),
      apiListTurnovers(scope !== undefined ? { propertyId: scope } : {}),
      apiListIssues(scope !== undefined ? { propertyId: scope } : {}),
      apiListChecklists(),
      apiListProperties(),
      apiListUnits(),
      apiOpsPropertyComparison(),
    ]);
  return {
    tasks,
    summary: asError(summaryR) === null ? (summaryR as OpsSummary) : null,
    overview: asError(overviewR) === null ? (overviewR as OpsOverview) : null,
    team,
    departments,
    turnovers,
    issues,
    checklists,
    properties,
    units,
    comparison,
  };
}

export default function OperationsScreen(): ReactElement {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [apiError, setApiError] = useState<UserMessageModel | null>(null);
  const [status, setStatus] = useState<UserMessageModel | null>(null);
  const [tab, setTab] = useState<OpsTab>("overview");
  const [todayIso, setTodayIso] = useState("");
  const [now, setNow] = useState<Date>(() => new Date(0));
  // Shared property scope preserved across tabs. "" = All properties.
  const [selectedProperty, setSelectedProperty] = useState<string>("");

  const [taskFilters, setTaskFilters] = useState<TaskFilters>(EMPTY_TASK_FILTERS);
  const [showNewTask, setShowNewTask] = useState(false);
  const [showNewIssue, setShowNewIssue] = useState(false);
  const [showNewChecklist, setShowNewChecklist] = useState(false);

  const [createBusy, setCreateBusy] = useState(false);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [stageBusyId, setStageBusyId] = useState<string | null>(null);
  const [issueBusyId, setIssueBusyId] = useState<string | null>(null);

  // Performance period (resolved on mount; SSR-safe).
  const [period, setPeriod] = useState<PeriodRange>({
    preset: "this-month",
    label: "This month",
    fromIso: "1970-01-01",
    toIso: "2999-12-31",
  });
  const [perf, setPerf] = useState<OpsPerformance | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const data = await loadAll(selectedProperty);
      setState({ kind: "ready", data });
    } catch {
      setState({ kind: "error" });
    }
  }, [selectedProperty]);

  // Initial mount: set clock + period, then load portfolio-wide. If the tenant
  // has exactly one property, auto-select it (no portfolio complexity needed).
  useEffect(() => {
    const d = new Date();
    setTodayIso(d.toISOString().slice(0, 10));
    setNow(d);
    setPeriod(resolvePeriod("this-month"));
    let active = true;
    loadAll("")
      .then((data) => {
        if (!active) {
          return;
        }
        if (data.properties.length === 1) {
          const only = data.properties[0];
          if (only !== undefined) {
            setSelectedProperty(only.id);
            // Reload scoped to the single property.
            void loadAll(only.id).then((scoped) => {
              if (active) {
                setState({ kind: "ready", data: scoped });
              }
            });
            return;
          }
        }
        setState({ kind: "ready", data });
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, []);

  // Reload all data whenever the selected property changes (after initial mount).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    let active = true;
    setState({ kind: "loading" });
    loadAll(selectedProperty)
      .then((data) => {
        if (active) {
          setState({ kind: "ready", data });
        }
      })
      .catch(() => {
        if (active) {
          setState({ kind: "error" });
        }
      });
    return () => {
      active = false;
    };
  }, [selectedProperty]);

  // Load performance whenever the period OR the selected property changes.
  useEffect(() => {
    if (period.fromIso === "1970-01-01") {
      return;
    }
    let active = true;
    apiOpsPerformance({
      fromIso: period.fromIso,
      toIso: period.toIso,
      ...(selectedProperty !== "" ? { propertyId: selectedProperty } : {}),
    })
      .then((r) => {
        if (active) {
          setPerf(asError(r) === null ? (r as OpsPerformance) : null);
        }
      })
      .catch(() => {
        if (active) {
          setPerf(null);
        }
      });
    return () => {
      active = false;
    };
  }, [period, selectedProperty]);

  // ---- Handlers -------------------------------------------------------------

  const runMutation = useCallback(
    async (
      fn: () => Promise<unknown>,
      okMsg: UserMessageModel,
      failWhat: string,
    ): Promise<void> => {
      setApiError(null);
      setStatus(null);
      try {
        const result = await fn();
        const err = asError(result);
        if (err !== null) {
          setApiError(toUserMessage(err));
          return;
        }
        setStatus(okMsg);
        await refresh();
      } catch {
        setApiError({
          what: failWhat,
          why: "The request to the operations service failed.",
          next: "Check the API server is running and try again.",
        });
      }
    },
    [refresh],
  );

  const handleCreateTask = useCallback(
    async (input: Parameters<typeof apiCreateOpsTask>[0]): Promise<void> => {
      setCreateBusy(true);
      await runMutation(
        () => apiCreateOpsTask(input),
        { what: "Task created.", why: "It has been added to the board.", next: "Assign or progress it as work happens." },
        "Could not create the task.",
      );
      setCreateBusy(false);
      setShowNewTask(false);
    },
    [runMutation],
  );

  const handleTaskStatus = useCallback(
    async (task: OpsTask, next: OpsTaskStatus): Promise<void> => {
      setStatusBusyId(task.id);
      let blockedReason: string | null = null;
      if (next === "blocked" && typeof window !== "undefined") {
        blockedReason = window.prompt("Reason this task is blocked?") ?? null;
      }
      await runMutation(
        () => apiUpdateOpsTaskStatus({ taskId: task.id, status: next, ...(blockedReason !== null ? { blockedReason } : {}) }),
        { what: "Task updated.", why: `"${task.title}" is now ${statusLabel(next)}.`, next: "No action required." },
        "Could not update the task.",
      );
      setStatusBusyId(null);
    },
    [runMutation],
  );

  const handleDeleteTask = useCallback(
    async (task: OpsTask): Promise<void> => {
      setDeletingId(task.id);
      await runMutation(
        () => apiDeleteOpsTask(task.id),
        { what: "Task deleted.", why: `"${task.title}" was removed.`, next: "No action required." },
        "Could not delete the task.",
      );
      setDeletingId(null);
    },
    [runMutation],
  );

  const handleTurnoverStage = useCallback(
    async (tv: OpsTurnover, stage: OpsTurnoverStage): Promise<void> => {
      setStageBusyId(tv.id);
      let blockedReason: string | null = null;
      if (stage === "blocked" && typeof window !== "undefined") {
        blockedReason = window.prompt("Reason this turnover is blocked?") ?? null;
      }
      await runMutation(
        () => apiUpdateTurnoverStage({ turnoverId: tv.id, stage, ...(blockedReason !== null ? { blockedReason } : {}) }),
        { what: "Turnover updated.", why: `${tv.unitName ?? "Unit"} is now ${stageLabel(stage)}.`, next: "No action required." },
        "Could not update the turnover.",
      );
      setStageBusyId(null);
    },
    [runMutation],
  );

  const handleCreateIssue = useCallback(
    async (input: Parameters<typeof apiCreateIssue>[0]): Promise<void> => {
      setCreateBusy(true);
      await runMutation(
        () => apiCreateIssue(input),
        { what: "Issue reported.", why: "It has been logged for follow-up.", next: input.createTask ? "A linked maintenance task was created." : "Assign or resolve it from the Issues tab." },
        "Could not report the issue.",
      );
      setCreateBusy(false);
      setShowNewIssue(false);
    },
    [runMutation],
  );

  const handleIssueStatus = useCallback(
    async (issue: OpsIssue, next: string): Promise<void> => {
      setIssueBusyId(issue.id);
      await runMutation(
        () => apiUpdateIssueStatus({ issueId: issue.id, status: next }),
        { what: "Issue updated.", why: `"${issue.title}" is now ${next.replace("_", " ")}.`, next: "No action required." },
        "Could not update the issue.",
      );
      setIssueBusyId(null);
    },
    [runMutation],
  );

  const handleCreateChecklist = useCallback(
    async (input: Parameters<typeof apiCreateChecklist>[0]): Promise<void> => {
      setCreateBusy(true);
      await runMutation(
        () => apiCreateChecklist(input),
        { what: "Checklist template created.", why: "It can now be attached to tasks and turnovers.", next: "No action required." },
        "Could not create the template.",
      );
      setCreateBusy(false);
      setShowNewChecklist(false);
    },
    [runMutation],
  );

  const handleDeleteChecklist = useCallback(
    async (t: OpsChecklistTemplate): Promise<void> => {
      setDeletingId(t.id);
      await runMutation(
        () => apiDeleteChecklist(t.id),
        { what: "Template deleted.", why: `"${t.name}" was removed.`, next: "No action required." },
        "Could not delete the template.",
      );
      setDeletingId(null);
    },
    [runMutation],
  );

  // Jump to Tasks with a status filter (from KPI cards / departments).
  const openTasksWithStatus = useCallback((s: OpsTaskStatus | "all"): void => {
    setTaskFilters({ ...EMPTY_TASK_FILTERS, status: s });
    setTab("tasks");
  }, []);
  const openTasksWithDepartment = useCallback((d: OpsDepartment): void => {
    setTaskFilters({ ...EMPTY_TASK_FILTERS, department: d });
    setTab("tasks");
  }, []);

  const data = state.kind === "ready" ? state.data : null;

  return (
    <section aria-labelledby="operations-heading" data-console-screen="operations">
      <div className="accounts-header">
        <div>
          <p className="console-page-eyebrow">Operations · Command centre</p>
          <h1 id="operations-heading">Operations</h1>
          <p className="console-lede">
            The real-time execution layer: what's happening today, whether
            properties are ready, what work is open or late, who owns it, and how
            operations are trending.
          </p>
        </div>
        {/* Shared property scope. Hidden entirely when the tenant has a single
            property (auto-selected) — no selector complexity in that case. */}
        {data !== null && data.properties.length > 1 ? (
          <div className="accounts-header__selector console-field ops-property-select">
            <label htmlFor="ops-property">Property</label>
            <select
              id="ops-property"
              value={selectedProperty}
              onChange={(e) => setSelectedProperty(e.target.value)}
            >
              <option value="">All properties</option>
              {data.properties.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {state.kind === "loading" ? (
        <p className="console-loading" role="status" aria-live="polite">Loading operations…</p>
      ) : null}

      {state.kind === "error" ? (
        <p className="console-empty" role="status">Operations data is unavailable right now. Try refreshing in a moment.</p>
      ) : null}

      {data !== null ? (
        <>
          {apiError !== null ? <UserMessage message={apiError} severity="error" /> : null}
          {status !== null ? <UserMessage message={status} severity="status" /> : null}

          <div className="crm-tabs" role="tablist" aria-label="Operations views">
            {OPS_TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? "crm-tab crm-tab--active" : "crm-tab"}
                onClick={() => setTab(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === "overview" ? (
            <>
              <KpiCards overview={data.overview} summary={data.summary} onOpenTasks={openTasksWithStatus} />
              {selectedProperty === "" && data.properties.length > 1 ? (
                <PropertyComparison rows={data.comparison} onOpenProperty={(id) => setSelectedProperty(id)} />
              ) : null}
              <PropertyReadiness readiness={data.overview?.readiness ?? []} />
              <NeedsAttention
                tasks={data.tasks}
                issues={data.issues}
                readiness={data.overview?.readiness ?? []}
                todayIso={todayIso}
                onView={() => setTab("tasks")}
              />
              <TodayTimeline overview={data.overview} tasks={data.tasks} todayIso={todayIso} />
              <DepartmentSnapshotPanel departments={data.departments} onOpenDepartment={openTasksWithDepartment} />
              <PerformancePanel perf={perf} period={period} onPeriodChange={setPeriod} />
            </>
          ) : null}

          {tab === "tasks" ? (
            <TasksTab
              tasks={data.tasks}
              todayIso={todayIso}
              filters={taskFilters}
              onFilters={setTaskFilters}
              properties={data.properties}
              onStatus={(task, s) => void handleTaskStatus(task, s)}
              onDelete={(task) => void handleDeleteTask(task)}
              onNewTask={() => setShowNewTask(true)}
              statusBusyId={statusBusyId}
              deletingId={deletingId}
            />
          ) : null}

          {tab === "turnovers" ? (
            <TurnoversTab
              turnovers={data.turnovers}
              now={now}
              onStage={(tv, s) => void handleTurnoverStage(tv, s)}
              stageBusyId={stageBusyId}
            />
          ) : null}

          {tab === "team" ? <TeamTab team={data.team} /> : null}

          {tab === "issues" ? (
            <IssuesTab
              issues={data.issues}
              onNewIssue={() => setShowNewIssue(true)}
              onStatus={(i, s) => void handleIssueStatus(i, s)}
              statusBusyId={issueBusyId}
            />
          ) : null}

          {tab === "checklists" ? (
            <ChecklistsTab
              templates={data.checklists}
              onNew={() => setShowNewChecklist(true)}
              onDelete={(t) => void handleDeleteChecklist(t)}
              deletingId={deletingId}
            />
          ) : null}

          {showNewTask ? (
            <NewTaskModal
              properties={data.properties}
              units={data.units}
              checklists={data.checklists}
              onClose={() => setShowNewTask(false)}
              onCreate={(input) => void handleCreateTask(input)}
              busy={createBusy}
            />
          ) : null}

          {showNewIssue ? (
            <NewIssueModal
              properties={data.properties}
              units={data.units}
              onClose={() => setShowNewIssue(false)}
              onCreate={(input) => void handleCreateIssue(input)}
              busy={createBusy}
            />
          ) : null}

          {showNewChecklist ? (
            <NewChecklistModal
              properties={data.properties}
              onClose={() => setShowNewChecklist(false)}
              onCreate={(input) => void handleCreateChecklist(input)}
              busy={createBusy}
            />
          ) : null}
        </>
      ) : null}
    </section>
  );
}
